import { useCallback, useRef } from "react";
import {
  playPronunciationSequence,
  playPronunciationTrack,
  stopPronunciation,
  type PronunciationAudioTrack,
} from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.audio";

export type ProgrammaticAudioTrack = PronunciationAudioTrack;

export type ProgrammaticAudioController = {
  playSequence: (tracks: readonly ProgrammaticAudioTrack[]) => Promise<void>;
  playTrack: (track: ProgrammaticAudioTrack) => Promise<void>;
  stop: () => void;
};

/**
 * General-purpose programmatic audio playback. Intended for the learn layout —
 * effects/handlers can call `playTrack` or `playSequence` to play audio
 * without an explicit user tap on a sound icon. Browse-layout playback must
 * NOT use this hook; route all browse playback through a Pressable + sound
 * icon instead.
 *
 * All playback shares a single app-wide native player (see
 * `shared-pronunciation-player`) so no ExoPlayer is allocated per hook mount.
 */
export function useProgrammaticAudio(options: { isMuted: boolean }): ProgrammaticAudioController {
  const { isMuted } = options;
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const getIsMuted = useCallback(() => isMutedRef.current, []);

  // NOTE: intentionally no stop-on-unmount. With the shared player, callers that
  // want playback to end on blur/unmount call `stop()` explicitly (e.g.
  // `useLearnMnemonicsProgrammaticAudio` on focus loss). The drill-reveal path
  // relies on audio surviving the front card being pushed away, so unmounting a
  // controller must never pause the shared player.

  const playTrack = useCallback(
    async (track: ProgrammaticAudioTrack) => {
      await playPronunciationTrack(track, getIsMuted);
    },
    [getIsMuted],
  );

  const playSequence = useCallback(
    async (tracks: readonly ProgrammaticAudioTrack[]) => {
      await playPronunciationSequence(tracks, getIsMuted);
    },
    [getIsMuted],
  );

  const stop = useCallback(() => {
    stopPronunciation();
  }, []);

  return { playSequence, playTrack, stop };
}
